import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel } from "@/components/common";
import { FilterSelect } from "@/components/BookingFilters";
import { InvoiceBuilder } from "@/components/InvoiceBuilder";
import { useFetch } from "@/hooks/useFetch";
import { useSettings } from "@/context/SettingsContext";
import { api, formatApiError } from "@/lib/api";
import { fmtDate, money } from "@/lib/format";
import { cn } from "@/lib/utils";

const INVOICE_STATUSES = ["Draft", "Sent", "Paid"];
const STATUS_CLS = { Draft: "bg-slate-100 text-slate-700", Sent: "bg-sky-100 text-sky-800", Paid: "bg-emerald-600 text-white" };

export const InvoiceStatus = ({ status }) => <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-semibold", STATUS_CLS[status])} data-testid="invoice-status">{status}</span>;

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [filters, setFilters] = useState({ company: "", status: "" });
  const [busy, setBusy] = useState(false);
  const { data: pending, reload: reloadPending } = useFetch("/invoices/pending");
  const { data: invoices, reload } = useFetch("/invoices", Object.fromEntries(Object.entries(filters).filter(([, v]) => v)));

  const openRunning = async (company) => {
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/running", { company });
      navigate(`/invoices/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const list = invoices || [];
  const sum = (s) => list.filter((i) => !s || i.status === s).reduce((a, i) => a + (s === "Paid" ? i.subtotal : i.balance), 0);
  const running = Object.fromEntries(list.filter((i) => i.running && i.status !== "Paid").map((i) => [i.company, i]));

  return (
    <div data-testid="invoices-page">
      <PageHeader title="Invoices" subtitle="Each company has one running invoice – every completed job stacks onto it until you mark it Sent, then a fresh one starts and you can mark the sent one Paid later. Use a date range only when you need a separate invoice for a specific week or month.">
        <Button onClick={() => { reload(); reloadPending(); }} disabled={busy} variant="outline" data-testid="btn-refresh-invoices"><RefreshCw className="h-4 w-4" /> Refresh</Button>
      </PageHeader>
      {settings.companies.length === 0 && <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="no-companies-hint">No account companies yet. Add them under <button className="underline" onClick={() => navigate("/settings")}>Settings → Companies</button>, then choose the company on each booking under “Bill to company”.</div>}
      <InvoiceBuilder onCreated={() => { reload(); reloadPending(); }} />
      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="pending-list">
        {settings.companies.map((c) => { const r = running[c.name]; return (
          <div key={c.name} className={cn("rounded-xl border bg-white p-4", r ? "border-sky-200" : "border-slate-200")} data-testid={`pending-${c.name}`}>
            <div className="flex items-start justify-between gap-2">
              <div><div className="font-display font-semibold text-[#0E1E38]">{c.name}</div><div className="text-xs text-slate-500">{r ? <span className="font-mono text-sky-700">{r.invoice_number} · running</span> : "No open invoice"}</div></div>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => (r ? navigate(`/invoices/${r.id}`) : openRunning(c.name))} data-testid={`btn-generate-${c.name}`}><FileText className="h-3.5 w-3.5" /> {r ? "Open" : "Start"}</Button>
            </div>
            <div className={cn("mt-3 font-mono text-xl font-bold", r && r.net_total < 0 && "text-rose-700")}>{r ? money(Math.abs(r.net_total)) : money(0)}</div>
            <div className="text-xs text-slate-500">{r ? `${r.lines.length} job${r.lines.length === 1 ? "" : "s"} · ${fmtDate(r.period_start, "d MMM")} – ${fmtDate(r.period_end, "d MMM")}${r.net_total < 0 ? " · we owe them" : ""}` : "Completed jobs will stack here automatically"}</div>
          </div>
        ); })}
      </div>
      <div className="mb-3 grid grid-cols-3 gap-3">
        {[["Draft", "text-slate-800"], ["Sent", "text-sky-800"], ["Paid", "text-emerald-700"]].map(([s, c]) => (
          <div key={s} className="rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={`invoice-total-${s}`}><div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{s}</div><div className={cn("font-mono text-lg font-bold", c)}>{money(sum(s))}</div></div>
        ))}
      </div>
      <div className="mb-3 flex flex-wrap gap-2">
        <FilterSelect id="inv-company" placeholder="Company" value={filters.company} options={settings.companies.map((c) => c.name)} onChange={(v) => setFilters({ ...filters, company: v })} />
        <FilterSelect id="inv-status" placeholder="Status" value={filters.status} options={INVOICE_STATUSES} onChange={(v) => setFilters({ ...filters, status: v })} />
      </div>
      <Panel>
        <table className="w-full text-sm" data-testid="invoices-table">
          <thead><tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">{["Invoice", "Company", "Period", "Jobs", "Amount", "Owed", "Due", "Status"].map((h) => <th key={h} className="pb-2 pr-3">{h}</th>)}</tr></thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-slate-500" data-testid="invoices-empty">No invoices yet. Mark a company job Completed or Paid and it will start that company's running invoice.</td></tr>}
            {list.map((i) => (
              <tr key={i.id} onClick={() => navigate(`/invoices/${i.id}`)} className="cursor-pointer border-t border-slate-100 hover:bg-sky-50/60" data-testid={`invoice-row-${i.id}`}>
                <td className="py-2.5 pr-3 font-mono font-semibold text-[#0E1E38]">{i.invoice_number}{i.running && i.status !== "Paid" && <span className="ml-2 rounded-full bg-sky-100 px-2 py-0.5 font-sans text-[10px] font-semibold text-sky-800" data-testid={`running-badge-${i.id}`}>Running</span>}</td>
                <td className="py-2.5 pr-3 font-medium">{i.company}</td>
                <td className="py-2.5 pr-3 text-slate-600">{fmtDate(i.period_start, "d MMM")} – {fmtDate(i.period_end, "d MMM yyyy")}</td>
                <td className="py-2.5 pr-3">{i.lines.length}</td>
                <td className="py-2.5 pr-3 font-mono font-semibold">{(i.net_total ?? i.subtotal) < 0 ? <span className="text-rose-700" title={`We owe ${i.company}`}>− {money(Math.abs(i.net_total))}</span> : money(i.net_total ?? i.subtotal)}</td>
                <td className={cn("py-2.5 pr-3 font-mono", i.balance > 0 ? "text-rose-700" : "text-emerald-700")}>{money(i.balance)}</td>
                <td className="py-2.5 pr-3 text-slate-600">{fmtDate(i.due_date, "d MMM yyyy")}</td>
                <td className="py-2.5"><InvoiceStatus status={i.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
